// src/pages/CategoriasRazas.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import GlassCard from '../components/UI/GlassCard';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import { 
  Tags, 
  Dog, 
  Plus, 
  Edit2, 
  Trash2, 
  Search, 
  Layers,
  CheckCircle2,
  AlertCircle,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import Tippy from '@tippyjs/react';

const CategoriasRazas = () => {
    const { can } = useAuth();
    const [activeTab, setActiveTab] = useState('categorias');
    const [loading, setLoading] = useState(true);
    
    // Data states
    const [categorias, setCategorias] = useState([]);
    const [razas, setRazas] = useState([]);
    const [especies, setEspecies] = useState([]);
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    
    // Form states
    const [formData, setFormData] = useState({
        nombre: '',
        color: '#8b5cf6',
        icono: '📋',
        especie_id: ''
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [cRes, rRes, eRes] = await Promise.all([
                axios.get('/backend/api/servicios/categorias.php'),
                axios.get('/backend/api/mascotas/razas.php'),
                axios.get('/backend/api/mascotas/especies.php')
            ]);
            
            if (cRes.data.success) setCategorias(cRes.data.data);
            if (rRes.data.success) setRazas(rRes.data.data);
            if (eRes.data.success) setEspecies(eRes.data.data);
        } catch (error) {
            toast.error('Error al sincronizar catálogos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                nombre: item.nombre,
                color: item.color || '#8b5cf6',
                icono: item.icono || '📋',
                especie_id: item.especie_id || ''
            });
        } else {
            setEditingItem(null);
            setFormData({
                nombre: '',
                color: '#8b5cf6',
                icono: '📋',
                especie_id: activeTab === 'razas' && especies.length > 0 ? especies[0].id : ''
            });
        }
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!formData.nombre) return toast.error('El nombre es obligatorio');
        if (activeTab === 'razas' && !formData.especie_id) return toast.error('Debe seleccionar una especie');

        try {
            const url = activeTab === 'categorias' 
                ? '/backend/api/servicios/categorias.php'
                : '/backend/api/mascotas/razas.php';
            
            const method = editingItem ? 'PUT' : 'POST';
            const data = editingItem ? { ...formData, id: editingItem.id } : formData;

            const response = await axios({ method, url, data });

            if (response.data.success) {
                toast.success(editingItem ? 'Actualizado con éxito' : 'Registrado con éxito');
                setShowModal(false);
                fetchData();
            } else {
                toast.error(response.data.message || 'Error en la operación');
            }
        } catch (error) {
            toast.error('Error de red o servidor');
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Confirmar eliminación?',
            text: "Esta acción podría fallar si hay registros vinculados (Servicios o Mascotas).",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Sí, eliminar',
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                const url = activeTab === 'categorias' 
                    ? `/backend/api/servicios/categorias.php?id=${id}`
                    : `/backend/api/mascotas/razas.php?id=${id}`;
                
                const response = await axios.delete(url);
                if (response.data.success) {
                    toast.success('Registro eliminado');
                    fetchData();
                } else {
                    toast.error(response.data.message || 'No se pudo eliminar');
                }
            } catch (error) {
                toast.error('Error al procesar eliminación');
            }
        }
    };

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1400px] mx-auto min-h-screen text-white">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-black flex items-center gap-4">
                        <Layers className="text-primary-light" size={42} /> 
                        Gestión de <span className="text-primary-light italic font-medium">Catálogos</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm font-medium flex items-center gap-2">
                         Configuración de diccionarios maestros del sistema
                    </p>
                </div>
                
                <div className="flex w-full md:w-auto gap-4">
                    <button 
                        onClick={() => handleOpenModal()}
                        className="premium-btn-primary flex items-center gap-3 px-8 shadow-xl shadow-primary/20"
                    >
                        <Plus size={20} />
                        <span>Añadir {activeTab === 'categorias' ? 'Categoría' : 'Raza'}</span>
                    </button>
                </div>
            </header>

            {/* Tabs de Navegación */}
            <div className="flex gap-4 mb-8 bg-white/5 p-1.5 rounded-2xl w-fit border border-white/5">
                <button 
                    onClick={() => setActiveTab('categorias')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'categorias' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                >
                    <Tags size={16} /> Categorías de Servicios
                </button>
                <button 
                    onClick={() => setActiveTab('razas')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'razas' ? 'bg-secondary text-white shadow-lg shadow-secondary/20' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                >
                    <Dog size={16} /> Razas por Especie
                </button>
            </div>

            <div className="grid grid-cols-1 gap-8">
                <GlassCard padding="0" className="shadow-2xl border-white/5 overflow-hidden min-h-[500px]">
                    {activeTab === 'categorias' ? (
                        <Table 
                            headers={['Icono', 'Nombre de Categoría', 'ID Color', 'Acciones']}
                            data={categorias.map(c => ({
                                _raw: c,
                                icono: <div className="text-2xl w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 border border-white/10">{c.icono}</div>,
                                nombre: <span className="font-bold text-lg">{c.nombre}</span>,
                                color: (
                                    <div className="flex items-center gap-3">
                                        <div className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: c.color }}></div>
                                        <code className="text-xs text-white/40">{c.color}</code>
                                    </div>
                                ),
                                acciones: (
                                    <div className="flex gap-2">
                                        <button onClick={() => handleOpenModal(c)} className="glass p-2 rounded-xl text-primary-light hover:bg-white/10"><Edit2 size={16}/></button>
                                        <button onClick={() => handleDelete(c.id)} className="glass p-2 rounded-xl text-red-400 hover:bg-red-500/10"><Trash2 size={16}/></button>
                                    </div>
                                )
                            }))}
                        />
                    ) : (
                        <Table 
                            headers={['Especie', 'Raza / Linaje', 'Estado', 'Acciones']}
                            data={razas.map(r => ({
                                _raw: r,
                                especie: (
                                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-primary-light">{r.especie_nombre}</span>
                                    </div>
                                ),
                                raza: <span className="font-bold text-white/90">{r.nombre}</span>,
                                estado: <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1"><CheckCircle2 size={10}/> Activo</div>,
                                acciones: (
                                    <div className="flex gap-2">
                                        <button onClick={() => handleOpenModal(r)} className="glass p-2 rounded-xl text-primary-light hover:bg-white/10"><Edit2 size={16}/></button>
                                        <button onClick={() => handleDelete(r.id)} className="glass p-2 rounded-xl text-red-400 hover:bg-red-500/10"><Trash2 size={16}/></button>
                                    </div>
                                )
                            }))}
                        />
                    )}
                </GlassCard>
            </div>

            {/* Modal de Edición/Creación */}
            <Modal
                show={showModal}
                onClose={() => setShowModal(false)}
                title={editingItem ? `Editando ${activeTab === 'categorias' ? 'Categoría' : 'Raza'}` : `Nueva ${activeTab === 'categorias' ? 'Categoría' : 'Raza'}`}
            >
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Nombre Descriptivo</label>
                        <input 
                            type="text" 
                            value={formData.nombre} 
                            onChange={e => setFormData({...formData, nombre: e.target.value})} 
                            className="input-field bg-slate-800/80" 
                            placeholder={activeTab === 'categorias' ? 'Ej. Corte y Estilo' : 'Ej. Pastor Alemán'} 
                        />
                    </div>

                    {activeTab === 'categorias' ? (
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Color de Identificación</label>
                                <div className="flex gap-4 items-center">
                                    <input 
                                        type="color" 
                                        value={formData.color} 
                                        onChange={e => setFormData({...formData, color: e.target.value})} 
                                        className="w-12 h-12 rounded-xl bg-white/5 border-none cursor-pointer" 
                                    />
                                    <code className="text-xs">{formData.color}</code>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Icono Emoji</label>
                                <input 
                                    type="text" 
                                    value={formData.icono} 
                                    onChange={e => setFormData({...formData, icono: e.target.value})} 
                                    className="input-field bg-slate-800/80 text-center text-xl" 
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Especie a la que pertenece</label>
                            <select 
                                value={formData.especie_id} 
                                onChange={e => setFormData({...formData, especie_id: e.target.value})} 
                                className="input-field bg-slate-800/80"
                            >
                                <option value="" disabled>Seleccione especie...</option>
                                {especies.map(e => <option key={e.id} value={e.id} className="bg-slate-900">{e.nombre}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="pt-6 border-t border-white/5 flex flex-col gap-4">
                        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 flex gap-3 items-start">
                            <AlertCircle size={16} className="text-primary-light shrink-0" />
                            <p className="text-[10px] text-primary-light font-medium leading-relaxed">
                                Asegúrese de que no existan duplicados para mantener la integridad de los reportes financieros y del catálogo.
                            </p>
                        </div>
                        <button 
                            onClick={handleSave}
                            className="premium-btn-primary w-full py-4 text-xs font-black uppercase tracking-widest"
                        >
                            {editingItem ? 'GUARDAR ACTUALIZACIÓN' : 'REGISTRAR EN CATÁLOGO'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CategoriasRazas;
